import cn from 'classnames';
import { EditableFields, Tabs } from 'Components';
import { Details, Permissions } from './tabs';
import styles from './EditRoleTabs.module.scss';
import { EditRoleBaseProps, EditRoleFormValues } from './EditRole.types';
import { useFormikContext } from 'formik';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PermissionGroupName } from 'Services/Api/Roles/Types';
import { ROLE_EDIT_I18N_FALLBACK } from './EditRole.constants';

export enum EditRoleTabKey {
	Details = 'Details',
	Permissions = 'Permissions',
	TrainingManager = 'TrainingManager',
	Lms = 'Lms'
}

const TAB_BY_KEY: Record<EditRoleTabKey, string> = {
	[EditRoleTabKey.Details]: 'role.editRole.tabs.details',
	[EditRoleTabKey.Permissions]: 'role.editRole.tabs.permissions',
	[EditRoleTabKey.TrainingManager]: 'role.editRole.tabs.trainingManager',
	[EditRoleTabKey.Lms]: 'role.editRole.tabs.lms'
};

function roleTabLabel(t: TFunction, tabKey: EditRoleTabKey) {
	const path = TAB_BY_KEY[tabKey];
	const fallback = ROLE_EDIT_I18N_FALLBACK[path];
	return fallback !== undefined ? t(path, { defaultValue: fallback }) : t(path);
}

function EditRoleTabs(props: EditRoleBaseProps) {
	const { t } = useTranslation();
	const {
		values: {
			data: { core, lms }
		}
	} = useFormikContext<EditRoleFormValues>();
	const { isNew } = props;

	const trainingGroupName = PermissionGroupName.TrainingManagerManagement;
	const corePermissionsTabGroups = core.filter((g) => g.name !== trainingGroupName);
	const trainingManagerPermissionGroups = core.filter((g) => g.name === trainingGroupName);

	return (
		<Tabs
			defaultActiveKey={EditRoleTabKey.Details}
			className={cn({
				[styles.tabsContentInner]: isNew
			})}
		>
			{!isNew && (
				<Tabs.TabPane
					tab={roleTabLabel(t, EditRoleTabKey.Details)}
					key={EditRoleTabKey.Details}
				>
					<EditableFields>
						<Details />
					</EditableFields>
				</Tabs.TabPane>
			)}

			{Boolean(corePermissionsTabGroups.length) && (
				<Tabs.TabPane
					tab={roleTabLabel(t, EditRoleTabKey.Permissions)}
					key={EditRoleTabKey.Permissions}
				>
					<EditableFields>
						<Permissions values={corePermissionsTabGroups} />
					</EditableFields>
				</Tabs.TabPane>
			)}

			{Boolean(trainingManagerPermissionGroups.length) && (
				<Tabs.TabPane
					tab={roleTabLabel(t, EditRoleTabKey.TrainingManager)}
					key={EditRoleTabKey.TrainingManager}
				>
					<EditableFields>
						<Permissions values={trainingManagerPermissionGroups} />
					</EditableFields>
				</Tabs.TabPane>
			)}

			{Boolean(lms.length) && (
				<Tabs.TabPane
					tab={roleTabLabel(t, EditRoleTabKey.Lms)}
					key={EditRoleTabKey.Lms}
				>
					<EditableFields>
						<Permissions values={lms} />
					</EditableFields>
				</Tabs.TabPane>
			)}
		</Tabs>
	);
}

export default EditRoleTabs;
